import React, { useRef, useMemo, useState, useEffect } from "react";
import { useListTrapSitesQuery } from "../../actions/trapsiteInQuery";
import { useListDevicesQuery } from "../../actions/devicesInQuery";
import { useListAlertsQuery } from "../../actions/alertsInQuery";
import { Alert } from "../../types";
import { DetectionStatusData } from "../../types/components/home";
import { DetectionActivity } from "./DetectionActivity";
import "./index.css";
import { useDetectionsAlerts } from "../../hooks/useAppAlerts";

const PAGE_SIZE = 20;

type Props = {
  // Extra props go here
};

const Index: React.FC<Props> = () => {
  const { data: devices, isLoading: devicesLoading } = useListDevicesQuery();
  const { data: trapSiteData, isLoading: trapSiteLoading } =
    useListTrapSitesQuery();
  // TODO: add pagination and filters to alerts query
  const deviceUuids =
    devices?.items.flatMap((device) => (device.uuid ? [device.uuid] : [])) ||
    [];
  const deviceUuidsKey = deviceUuids.join(",");

  const [page, setPage] = useState(0);
  const [allAlerts, setAllAlerts] = useState<Alert[]>([]);

  const {
    data: alerts,
    isLoading: alertsLoading,
    hasDevices,
    isFetching,
  } = useDetectionsAlerts({
    page: page,
    pageSize: PAGE_SIZE,
  });

  // Reset alerts when the device list changes
  useEffect(() => {
    setPage(0);
    setAllAlerts([]);
  }, [deviceUuidsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // when pages are fetched, append to the list
  useEffect(() => {
    if (!alerts?.items) return;
    if (page === 0) {
      setAllAlerts(alerts.items);
    } else {
      setAllAlerts((prev) => [...prev, ...alerts.items]);
    }
  }, [alerts]); // eslint-disable-line react-hooks/exhaustive-deps

  const listContainerRef = useRef<HTMLDivElement>(null);

  const hasMore = allAlerts.length < (alerts?.count ?? 0);

  // Scroll listener for infinite scrolling
  useEffect(() => {
    const el = listContainerRef.current;
    if (!el) return;

    const onScroll = () => {
      if (isFetching || !hasMore) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
        setPage((prev) => prev + 1);
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [isFetching, hasMore]);

  const handleClickAlert = (id: string) => {
    // TODO
  };

  const joinedAlerts = useMemo(() => {
    if (!allAlerts.length || !hasDevices) {
      return [];
    }
    let result: DetectionStatusData[] = [];

    allAlerts.forEach((alert) => {
      if (alert.tag !== "Detection" && alert.tag !== "Invasive") {
        return;
      }
      const device = devices?.items?.find((d) => d.uuid === alert.device);
      const trapSite = trapSiteData?.items?.find((t) => {
        return t._id === device?.trap_site_id;
      });
      result.push({
        _id: alert._id,
        message:
          alert.tag === "Detection"
            ? "Mosquito detected"
            : "Invasive mosquito detected",
        device_name: device?.display_name || "",
        trapsite_name: trapSite?.display_name,
        created_at: alert.created_at,
      });
    });

    return result;
  }, [allAlerts, devices, trapSiteData, hasDevices]);

  return (
    <DetectionActivity
      loading={
        trapSiteLoading || devicesLoading || (alertsLoading && page === 0)
      }
      isFetchingMore={isFetching && page > 0}
      listContainerRef={listContainerRef}
      joinedAlerts={joinedAlerts}
      handleClickAlert={handleClickAlert}
    />
  );
};

export default Index;
